import { useState, useMemo } from "react";
import { LabelWithHover } from "@/components/ui/LabelWithHover";
import { useZoneLabels } from "@/hooks/use-zone-labels";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Link } from "wouter";
import { Search, ExternalLink, MapPin, Dog, Cat, PawPrint, BarChart2, List, TrendingUp } from "lucide-react";
import { AnimalAvatar } from "@/components/animals/animal-avatar";
import { AnimalActionMenu } from "@/components/animal-action-menu";
import { getKennelStatusBadgeClass, formatKennelStatus } from "@/lib/badge-styles";
import { computeDaysInShelter, getDaysInShelterColor } from "@/lib/animal-utils";
import { MetricChartView } from "./MetricChartView";

interface MetricAnimal {
  id: string;
  animalId: string;
  name: string;
  species: string;
  photoUrl: string | null;
  location: string;
  zoneCode: string;
  kennelStatusNorm?: string;
  substatusNorm?: string;
  isOutcomed: boolean;
  adoptionLevelNorm?: string | null;
  evaluation?: { status: string | null; label: string | null } | null;
  isOnHold?: boolean | null;
  intakeDate?: string | null;
}

interface MetricResultsResponse {
  animals: MetricAnimal[];
  total: number;
  page: number;
  limit: number;
}

interface MetricConditionLeaf {
  field: string;
  operator?: string;
  value?: unknown;
}

interface MetricConditionTree {
  operator?: string;
  conditions: MetricConditionLeaf[];
}

interface MetricWidgetProps {
  metricId: string;
  label: string;
  description?: string | null;
  locationOverride?: string;
  displayFields?: string[];
  defaultSortField?: string | null;
  defaultSortDirection?: string | null;
  chartEnabled?: boolean;
  defaultView?: string;
  resultType?: string;
  conditions?: MetricConditionTree;
}

const LOCATION_LABELS: Record<string, string> = {
  SALEM: "Salem",
  BOSTON: "Boston",
  METHUEN: "Methuen",
  CAPE: "Cape",
};

const SPECIES_ICONS: Record<string, typeof Dog> = {
  DOG: Dog,
  CAT: Cat,
};

export function MetricWidget({
  metricId,
  label,
  description,
  locationOverride,
  displayFields = [],
  chartEnabled = false,
  defaultView = "table",
  resultType = "list",
  conditions,
}: MetricWidgetProps) {
  const isCountOnly = resultType === "count";
  const { resolveLabel } = useZoneLabels();

  // Flat list of field names used in this metric's conditions — passed to
  // MetricChartView to suppress redundant group-by and comparison options.
  const conditionFields = conditions?.conditions.map((c) => c.field) ?? [];

  const [view, setView] = useState<"table" | "chart">(() => {
    if (isCountOnly) return "chart";
    if (chartEnabled && defaultView === "chart") return "chart";
    return "table";
  });

  const [search, setSearch] = useState("");
  const [speciesFilter, setSpeciesFilter] = useState<string | null>(null);
  const [locationFilter, setLocationFilter] = useState<string | null>(null);

  const showChartToggle = chartEnabled && !isCountOnly;

  const queryKey = locationOverride
    ? ["/api/metrics", metricId, "results", locationOverride]
    : ["/api/metrics", metricId, "results"];

  const { data, isLoading } = useQuery<MetricResultsResponse>({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams({ limit: "200" });
      if (locationOverride) params.set("locationOverride", locationOverride);
      const res = await fetch(`/api/metrics/${metricId}/results?${params}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch metric results");
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
    enabled: !isCountOnly,
  });

  const allAnimals = data?.animals ?? [];
  const total = data?.total ?? 0;

  const filteredAnimals = useMemo(() => {
    let result = allAnimals;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(
        (a) => a.name.toLowerCase().includes(q) || a.animalId.toLowerCase().includes(q),
      );
    }
    if (speciesFilter) {
      result = result.filter((a) => a.species === speciesFilter);
    }
    if (locationFilter) {
      result = result.filter((a) => a.location === locationFilter);
    }
    return result;
  }, [allAnimals, search, speciesFilter, locationFilter]);

  const availableSpecies = useMemo(() => {
    const set = new Set(allAnimals.map((a) => a.species));
    return Array.from(set).sort();
  }, [allAnimals]);

  const availableLocations = useMemo(() => {
    if (locationOverride) return [];
    const set = new Set(allAnimals.map((a) => a.location));
    return Array.from(set).sort();
  }, [allAnimals, locationOverride]);

  const viewAllUrl = locationOverride
    ? `/animals?metricId=${metricId}&location=${locationOverride}`
    : `/animals?metricId=${metricId}`;

  const displayAnimals = filteredAnimals.slice(0, 8);

  const showLocation = displayFields.length === 0 || displayFields.includes("location");
  const showDays = displayFields.includes("days_in_shelter");
  const showLevel = displayFields.includes("adoption_level");
  const showEval = displayFields.includes("evaluation_status");
  const showHold = displayFields.includes("on_hold");

  return (
    <Card data-testid={`widget-metric-${metricId}`}>
      <CardHeader className="pb-2 flex flex-row items-start justify-between gap-2 flex-wrap space-y-0">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            {isCountOnly ? (
              <TrendingUp className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            ) : (
              <BarChart2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            )}
            <CardTitle className="text-base font-semibold truncate">{label}</CardTitle>
            {!isCountOnly && (
              isLoading ? (
                <Skeleton className="h-5 w-10 rounded-full flex-shrink-0" />
              ) : (
                <Badge variant="secondary" className="flex-shrink-0" data-testid={`badge-metric-count-${metricId}`}>
                  {total}
                </Badge>
              )
            )}
          </div>
          {description && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{description}</p>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {showChartToggle && (
            <>
              <Button
                variant={view === "table" ? "default" : "outline"}
                size="sm"
                onClick={() => setView("table")}
                data-testid={`button-view-table-${metricId}`}
              >
                <List className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant={view === "chart" ? "default" : "outline"}
                size="sm"
                onClick={() => setView("chart")}
                data-testid={`button-view-chart-${metricId}`}
              >
                <BarChart2 className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
          {!isCountOnly && (
            <Button
              variant="ghost"
              size="sm"
              asChild
              data-testid={`button-view-all-${metricId}`}
            >
              <Link href={viewAllUrl}>
                View all {total}
                <ExternalLink className="ml-1.5 h-3 w-3" />
              </Link>
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-2">
        {isCountOnly || view === "chart" ? (
          <MetricChartView metricId={metricId} locationOverride={locationOverride} conditionFields={conditionFields} conditions={conditions} />
        ) : (
          <>
            <div className="relative w-full">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Name or ID…"
                className="pl-8 h-8 text-sm w-full"
                data-testid={`input-metric-search-${metricId}`}
              />
            </div>
            {availableSpecies.length > 1 && (
              <div className="flex flex-wrap gap-1">
                {availableSpecies.map((s) => {
                  const Icon = SPECIES_ICONS[s] ?? PawPrint;
                  return (
                    <Button
                      key={s}
                      variant={speciesFilter === s ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSpeciesFilter(speciesFilter === s ? null : s)}
                      data-testid={`filter-species-${s}-${metricId}`}
                    >
                      <Icon className="h-3 w-3" />
                      <span className="ml-1 capitalize">{s.toLowerCase()}</span>
                    </Button>
                  );
                })}
              </div>
            )}
            {availableLocations.length > 1 && (
              <div className="flex flex-wrap gap-1">
                {availableLocations.map((loc) => (
                  <Button
                    key={loc}
                    variant={locationFilter === loc ? "default" : "outline"}
                    size="sm"
                    onClick={() => setLocationFilter(locationFilter === loc ? null : loc)}
                    data-testid={`filter-location-${loc}-${metricId}`}
                  >
                    {LOCATION_LABELS[loc] ?? loc}
                  </Button>
                ))}
              </div>
            )}

            {isLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-12 w-full rounded-md" />
                ))}
              </div>
            ) : filteredAnimals.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-sm text-muted-foreground" data-testid={`empty-metric-${metricId}`}>
                {total === 0 ? "No animals match this count" : "No animals match your search"}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto rounded-md border border-border">
                  <Table className="table-zebra" data-testid={`list-metric-animals-${metricId}`}>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs w-12 pl-3">Pet</TableHead>
                        <TableHead className="text-xs">Name</TableHead>
                        {showLocation && <TableHead className="text-xs">Location</TableHead>}
                        {showDays && <TableHead className="text-xs text-right">LOS</TableHead>}
                        {showLevel && <TableHead className="text-xs text-center">Level</TableHead>}
                        {showEval && <TableHead className="text-xs">Eval Status</TableHead>}
                        {showHold && <TableHead className="text-xs">Hold</TableHead>}
                        <TableHead className="text-xs">Status</TableHead>
                        <TableHead className="w-9 pr-3"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {displayAnimals.map((animal) => {
                        const days = showDays ? computeDaysInShelter(animal.intakeDate ?? null) : null;

                        return (
                          <TableRow
                            key={animal.id}
                            className="cursor-pointer"
                            data-testid={`row-metric-animal-${animal.id}`}
                          >
                            <TableCell className="py-2 pl-3">
                              <Link href={`/animals/${animal.animalId}`}>
                                <AnimalAvatar
                                  animalId={animal.animalId}
                                  photoUrl={animal.photoUrl}
                                  name={animal.name}
                                  species={animal.species as "DOG" | "CAT" | "OTHER"}
                                  className="h-8 w-8"
                                />
                              </Link>
                            </TableCell>
                            <TableCell className="py-2">
                              <Link href={`/animals/${animal.animalId}`} className="block min-w-[120px]">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-sm font-medium">{animal.name}</span>
                                  <span className="text-xs text-muted-foreground font-mono">{animal.animalId}</span>
                                </div>
                                <div className="text-xs text-muted-foreground capitalize">
                                  {animal.species.toLowerCase()}
                                </div>
                              </Link>
                            </TableCell>
                            {showLocation && (
                              <TableCell className="py-2" data-testid={`field-location-${animal.id}`}>
                                <Link href={`/animals/${animal.animalId}`} className="block min-w-[90px]">
                                  <div className="flex items-center gap-1 text-xs">
                                    <MapPin className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                    <span>{LOCATION_LABELS[animal.location] ?? animal.location}</span>
                                  </div>
                                  <div className="text-xs text-muted-foreground ml-4">{resolveLabel(animal.zoneCode)}</div>
                                </Link>
                              </TableCell>
                            )}
                            {showDays && (
                              <TableCell className="py-2 text-right" data-testid={`field-days-${animal.id}`}>
                                {days !== null ? (
                                  <span className={`text-xs font-semibold tabular-nums ${getDaysInShelterColor(days)}`}>
                                    {days}d
                                  </span>
                                ) : (
                                  <span className="text-xs text-muted-foreground">—</span>
                                )}
                              </TableCell>
                            )}
                            {showLevel && (
                              <TableCell className="py-2 text-center" data-testid={`field-adoption-${animal.id}`}>
                                {animal.adoptionLevelNorm ? (
                                  <span className="text-xs text-muted-foreground font-mono">
                                    {animal.adoptionLevelNorm}
                                  </span>
                                ) : (
                                  <span className="text-xs text-muted-foreground">—</span>
                                )}
                              </TableCell>
                            )}
                            {showEval && (
                              <TableCell className="py-2" data-testid={`field-eval-${animal.id}`}>
                                {animal.evaluation?.status ? (
                                  <span className="text-xs text-muted-foreground">
                                    <LabelWithHover label={animal.evaluation.label ?? animal.evaluation.status ?? ""} hoverContent={null} />
                                  </span>
                                ) : (
                                  <span className="text-xs text-muted-foreground">—</span>
                                )}
                              </TableCell>
                            )}
                            {showHold && (
                              <TableCell className="py-2" data-testid={`field-hold-${animal.id}`}>
                                {animal.isOnHold ? (
                                  <Badge variant="outline" className="text-xs">On hold</Badge>
                                ) : null}
                              </TableCell>
                            )}
                            <TableCell className="py-2">
                              {animal.kennelStatusNorm && (
                                <Badge
                                  variant="outline"
                                  className={`text-xs ${getKennelStatusBadgeClass(animal.kennelStatusNorm)}`}
                                >
                                  {formatKennelStatus(animal.kennelStatusNorm, animal.substatusNorm)}
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="py-2 pr-3">
                              <AnimalActionMenu
                                animalId={animal.animalId}
                                animalName={animal.name}
                                animalSpecies={animal.species}
                                animalPhotoUrl={animal.photoUrl}
                                navigationOnly
                                data-testid={`menu-animal-${animal.id}`}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                {filteredAnimals.length > 8 && (
                  <div className="text-center">
                    <Button variant="ghost" size="sm" asChild>
                      <Link href={viewAllUrl} data-testid={`button-view-more-${metricId}`}>
                        +{filteredAnimals.length - 8} more — View all {total}
                        <ExternalLink className="ml-1.5 h-3 w-3" />
                      </Link>
                    </Button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
